import type { PulseMessage } from "@/schemas/thread.schema";
import type { RequestConfig } from "@/services/api";
import { api } from "@/services/api";

/**
 * Gateway contract: a chunk may carry only `reasoning_content`, with no `content`
 * at all, while the model is still thinking.
 */
export type ChunkDelta = PulseMessage & { reasoning_content?: string };

interface ChunkChoice {
  delta: ChunkDelta;

  finish_reason: "stop" | "length" | "tool_calls" | "content_filter" | "function_call" | null;

  index: number;
}

/** Present only when the provider reported it. Never a synthesized zero. */
export interface OutputTokensDetails {
  reasoning_tokens: number;
}

/** Present only when the provider reported it. Never a synthesized zero. */
export interface InputTokensDetails {
  cached_tokens?: number;
  cache_write_tokens?: number;
}

export interface StreamUsage {
  input_tokens?: number;
  output_tokens?: number;
  output_tokens_details?: OutputTokensDetails;
  input_tokens_details?: InputTokensDetails;
}

export interface StreamPerformance {
  /** First chunk of any kind - with reasoning on, it lands before the answer. */
  ttfb_ms?: number;
  first_reasoning_ms?: number;
  first_content_ms?: number;
}

export interface ChatCompletionChunk {
  id: string;

  /** Empty on the synthetic final chunk, which is the one carrying usage. */
  choices: Array<ChunkChoice>;

  usage?: StreamUsage;

  performance?: StreamPerformance;

  created: number;

  model: string;

  object: "chat.completion.chunk";

  system_fingerprint?: string;
}

export interface TextContentPart {
  type: "text";
  text: string;
}

export interface ImageContentPart {
  type: "image_url";
  image_url: { url: string };
}

export interface FileContentPart {
  type: "file";
  file: { file_data: string; filename: string };
}

export type ContentPart = TextContentPart | ImageContentPart | FileContentPart;

export function isTextPart(part: ContentPart): part is TextContentPart {
  return part.type === "text";
}

export function isImagePart(part: ContentPart): part is ImageContentPart {
  return part.type === "image_url";
}

export function isFilePart(part: ContentPart): part is FileContentPart {
  return part.type === "file";
}

export interface CompletionMessage {
  role: "system" | "user" | "assistant";
  content: string | ContentPart[];
  name?: string;
}

export interface CompletionRequest {
  model: string;
  messages: CompletionMessage[];
  seed?: number;
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  top_k?: number;
  do_sample?: boolean;
  stream?: boolean;
  stop?: string | string[];
  frequency_penalty?: number;
  presence_penalty?: number;
}

interface FunctionCall {
  arguments: string;
  name: string;
}

interface ChatCompletionMessageToolCall {
  id: string;
  function: FunctionCall;
  type: "function";
}

type ChatCompletionMessageParam =
  | {
      role: "system";
      content: string;
      name?: string;
    }
  | {
      role: "user";
      content: string | ContentPart[];
      name?: string;
    }
  | {
      role: "assistant";
      content?: string;
      function_call?: FunctionCall;
      name?: string;
      tool_calls?: Array<ChatCompletionMessageToolCall>;
    }
  | {
      role: "function";
      content: string | null;
      name: string;
    }
  | {
      role: "tool";
      content: string;
      tool_call_id: string;
    };

interface Choice {
  finish_reason?: "stop" | "length" | "tool_calls" | "content_filter" | "function_call";
  message: ChatCompletionMessageParam;
}

interface CompletionUsage {
  input_tokens: number;
  output_tokens: number;
}

export interface ChatCompletion {
  choices: Choice[];
  created: number;
  model: string;
  usage: CompletionUsage;
}

async function* handleLLMStream(
  stream: ReadableStream<Uint8Array>
): AsyncGenerator<ChatCompletionChunk> {
  const reader = stream.getReader();
  const decoder = new TextDecoder("utf-8");

  let done = false;

  while (!done) {
    const { value, done: readerDone } = await reader.read();
    done = readerDone;

    if (value) {
      const jsonString = decoder
        .decode(value)
        .split("data: ")
        .map((v) => v.trim())
        .filter((v) => {
          if (v === "[DONE]") return false;
          return v.length > 0;
        });

      try {
        for (const json of jsonString) {
          const jsonObject = JSON.parse(json);

          // Check if the response contains an error (specific to LLM API)
          if (jsonObject.error) {
            const error = new Error(jsonObject.error);
            error.name = "LLMStreamError";
            throw error;
          }

          yield jsonObject as ChatCompletionChunk;
        }
      } catch (error) {
        if (error instanceof Error && error.name === "LLMStreamError") {
          throw error;
        }
        console.warn("Failed to parse JSON:", error);
      }
    }
  }

  // Close the reader
  reader.releaseLock();
}

export const llmApi = {
  async completion(body: CompletionRequest, config?: RequestConfig): Promise<ChatCompletion> {
    const { data } = await api.post(
      "/llms/completion",
      {
        ...body,
        stream: false,
      },
      config
    );

    return data;
  },

  async stream(body: CompletionRequest, config?: RequestConfig) {
    const abortController = new AbortController();
    const response = await api.post(`/llms/completion`, body, {
      responseType: "stream",
      signal: abortController.signal,
      ...config,
      headers: { ...config?.headers },
    });

    const data = response.data as ReadableStream<any>;

    const stream = handleLLMStream(data);

    return { stream, abortController };
  },
};
